/**
 * Theme colours for the Skia scene: `useCSSVariable("--color-…")` reports a token as HSL channels
 * (`"0 0% 9%"`), or as a resolved `hsl(…)`, `rgb(…)` or hex string, and the scene's UI-thread
 * frame wants opaque 0xAARRGGBB numbers it can mix per channel.
 */

const SEPARATORS = /[\s,/]+/;
const FUNCTIONAL_COLOR = /^(hsla?|rgba?)\((.*)\)$/i;
const SHORT_HEX = /^[\da-f]{3,4}$/i;
const LONG_HEX = /^[\da-f]{6,8}$/i;

function hslToRgb(hue: number, saturation: number, lightness: number): [number, number, number] {
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const sector = (((hue % 360) + 360) % 360) / 60;
  const x = chroma * (1 - Math.abs((sector % 2) - 1));
  const [red, green, blue] =
    sector < 1
      ? [chroma, x, 0]
      : sector < 2
        ? [x, chroma, 0]
        : sector < 3
          ? [0, chroma, x]
          : sector < 4
            ? [0, x, chroma]
            : sector < 5
              ? [x, 0, chroma]
              : [chroma, 0, x];
  const match = lightness - chroma / 2;
  return [red + match, green + match, blue + match];
}

function argb(red: number, green: number, blue: number): number {
  const byte = (channel: number) => Math.max(0, Math.min(255, Math.round(channel * 255)));
  return (0xff_00_00_00 | (byte(red) << 16) | (byte(green) << 8) | byte(blue)) >>> 0;
}

/** The first three numbers of `"0 0% 9%"`, `"0, 0%, 9%"` or `"10 20 30 / 0.5"`, if they are numbers. */
function parseChannels(channels: string): [number, number, number] | null {
  const [first, second, third] = channels
    .trim()
    .split(SEPARATORS)
    .map((part) => Number.parseFloat(part));
  if (first === undefined || second === undefined || third === undefined) {
    return null;
  }
  if (!Number.isFinite(first) || !Number.isFinite(second) || !Number.isFinite(third)) {
    return null;
  }
  return [first, second, third];
}

/** `"0 0% 9%"` → 0xAARRGGBB. */
function parseHslChannels(channels: string): number | null {
  const parsed = parseChannels(channels);
  if (parsed === null) {
    return null;
  }
  const [hue, saturation, lightness] = parsed;
  return argb(...hslToRgb(hue, saturation / 100, lightness / 100));
}

function parseHex(hex: string): number | null {
  const digits = SHORT_HEX.test(hex)
    ? Array.from(hex.slice(0, 3), (digit) => digit + digit).join("")
    : LONG_HEX.test(hex)
      ? hex.slice(0, 6)
      : null;
  if (digits === null) {
    return null;
  }
  return (0xff_00_00_00 | Number.parseInt(digits, 16)) >>> 0;
}

/**
 * A theme colour as `useCSSVariable` reports it, as an opaque 0xAARRGGBB, or `null` for anything
 * it cannot read (then the palette's own guess stands in).
 */
export function welcomeGalaxyThemeColor(value: string | number | undefined): number | null {
  if (value === undefined || value === "") {
    return null;
  }
  if (typeof value === "number") {
    return (0xff_00_00_00 | (value & 0xff_ff_ff)) >>> 0;
  }
  const text = value.trim();
  if (text.startsWith("#")) {
    return parseHex(text.slice(1));
  }
  const functional = FUNCTIONAL_COLOR.exec(text);
  if (functional === null) {
    return parseHslChannels(text);
  }
  const [, name = "", inner = ""] = functional;
  if (name.toLowerCase().startsWith("hsl")) {
    return parseHslChannels(inner);
  }
  const parsed = parseChannels(inner);
  if (parsed === null) {
    return null;
  }
  const [red, green, blue] = parsed;
  return argb(red / 255, green / 255, blue / 255);
}
